import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Divider,
  Button,
  ButtonGroup,
  Chip,
  Stack,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Notifications as AlertIcon,
  Person as PersonIcon,
  Diamond as DiamondIcon,
  LocalShipping as TransitIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Visibility as ViewIcon,
  TrendingUp as TrendingUpIcon,
  LocationOn as LocationIcon,
  PriorityHigh as PriorityHighIcon
} from '@mui/icons-material';
import { mockStatistics, mockAlerts, mockAssets, mockUsers } from '../../mock/mockData';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';

// For the heat map
import { GoogleMap, HeatmapLayer } from '@react-google-maps/api';
import { useGoogleMapsLoader } from '../../utils/googleMapsLoader';

const DashboardStats = () => {
  const [timeRange, setTimeRange] = useState('week');
  const [stats, setStats] = useState(mockStatistics);
  const [recentAlerts, setRecentAlerts] = useState([]);
  const [assetsInTransit, setAssetsInTransit] = useState([]);
  const [panicAlerts, setPanicAlerts] = useState([]);
  const [mapLoaded, setMapLoaded] = useState(false);

  // Load Google Maps API
  const { isLoaded } = useGoogleMapsLoader();

  useEffect(() => {
    // Get recent alerts sorted by timestamp
    const sortedAlerts = [...mockAlerts].sort((a, b) => 
      new Date(b.timestamp) - new Date(a.timestamp)
    ).slice(0, 5);
    
    setRecentAlerts(sortedAlerts);
    
    // Get assets in transit
    const transitAssets = mockAssets.filter(asset => asset.inTransit);
    setAssetsInTransit(transitAssets);
    
    // Get panic alerts
    const panicAlerts = mockAlerts.filter(alert => 
      alert.type === 'PANIC' && alert.status === 'active'
    );
    setPanicAlerts(panicAlerts);
  }, []);

  // Colors for pie charts
  const COLORS = ['#155EEF', '#039855', '#F79009', '#F04438', '#6941C6'];
  
  // Prepare data for alert type pie chart
  const alertTypeData = Object.entries(stats.alertsByType).map(([key, value]) => ({
    name: key,
    value
  }));
  
  // Prepare data for asset type pie chart
  const assetTypeData = Object.entries(stats.assetsByType).map(([key, value]) => ({
    name: key,
    value
  }));
  
  // Prepare data for alerts over time
  const alertsOverTimeData = stats.alertsOverTime;
  
  // Prepare data for user activity
  const userActivityData = stats.userActivity.map(user => {
    const userData = mockUsers.find(u => u.id === user.userId);
    return {
      name: userData ? userData.name : user.userId,
      alerts: user.alertCount,
      assets: user.assetsCount
    };
  });

  // Prepare heat map data
  const heatmapData = isLoaded ? stats.locationHotspots.flatMap(hotspot => 
    Array(hotspot.count).fill({
      location: new window.google.maps.LatLng(
        hotspot.coordinates.lat,
        hotspot.coordinates.lng
      )
    })
  ) : [];

  // Custom formatter for recharts tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper sx={{ p: 1.5, boxShadow: 2 }}>
          <Typography variant="subtitle2">{label}</Typography>
          {payload.map((entry, index) => (
            <Typography 
              key={`tooltip-${index}`} 
              variant="body2" 
              sx={{ color: entry.color }}
            >
              {entry.name}: {entry.value}
            </Typography>
          ))}
        </Paper>
      );
    }
    return null;
  };

  // Render name with avatar
  const renderNameWithAvatar = (name) => (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Avatar sx={{ width: 24, height: 24, mr: 1, fontSize: '0.75rem' }}>
        {name.charAt(0)}
      </Avatar>
      <Typography variant="body2" noWrap>{name}</Typography>
    </Box>
  );

  return (
    <Box>
      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'primary.50', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                  <DiamondIcon />
                </Avatar>
                <Typography variant="h6" color="primary.800">Assets</Typography>
              </Box>
              <Typography variant="h3" color="primary.800" sx={{ mb: 1 }}>
                {stats.totalAssets}
              </Typography>
              <Typography variant="body2" color="primary.700">
                Total value: ${stats.totalValue.toLocaleString()}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <Chip 
                  size="small" 
                  icon={<TransitIcon fontSize="small" />} 
                  label={`${stats.assetsInTransit} in transit`} 
                  sx={{ bgcolor: 'primary.100', color: 'primary.800' }} 
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'success.50', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                  <PersonIcon />
                </Avatar>
                <Typography variant="h6" color="success.800">Users</Typography>
              </Box>
              <Typography variant="h3" color="success.800" sx={{ mb: 1 }}>
                {stats.totalUsers}
              </Typography>
              <Typography variant="body2" color="success.700">
                {mockUsers.filter(user => user.status === 'active').length} active users
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <Chip 
                  size="small" 
                  icon={<CheckIcon fontSize="small" />} 
                  label="All users online" 
                  sx={{ bgcolor: 'success.100', color: 'success.800' }} 
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'warning.50', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: 'warning.main', mr: 2 }}>
                  <AlertIcon />
                </Avatar>
                <Typography variant="h6" color="warning.800">Alerts</Typography>
              </Box>
              <Typography variant="h3" color="warning.800" sx={{ mb: 1 }}>
                {stats.activeAlerts + stats.resolvedAlerts}
              </Typography>
              <Typography variant="body2" color="warning.700">
                {stats.activeAlerts} active alerts
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <Chip 
                  size="small" 
                  icon={<WarningIcon fontSize="small" />} 
                  label={`${panicAlerts.length} panic alerts`} 
                  sx={{ bgcolor: 'error.100', color: 'error.800' }} 
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'gray.50', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: 'gray.700', mr: 2 }}>
                  <LocationIcon />
                </Avatar>
                <Typography variant="h6" color="gray.800">Locations</Typography>
              </Box>
              <Typography variant="h3" color="gray.800" sx={{ mb: 1 }}>
                {stats.locationHotspots.length}
              </Typography>
              <Typography variant="body2" color="gray.700">
                Tracking in {stats.locationHotspots.length} areas
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <Chip 
                  size="small" 
                  icon={<TrendingUpIcon fontSize="small" />} 
                  label="All areas covered" 
                  sx={{ bgcolor: 'gray.200', color: 'gray.800' }} 
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">Alerts Over Time</Typography>
              <ButtonGroup size="small" variant="outlined">
                <Button 
                  onClick={() => setTimeRange('week')}
                  variant={timeRange === 'week' ? 'contained' : 'outlined'}
                >
                  Week
                </Button>
                <Button 
                  onClick={() => setTimeRange('month')}
                  variant={timeRange === 'month' ? 'contained' : 'outlined'}
                >
                  Month
                </Button>
                <Button 
                  onClick={() => setTimeRange('year')}
                  variant={timeRange === 'year' ? 'contained' : 'outlined'}
                >
                  Year
                </Button>
              </ButtonGroup>
            </Box>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart
                data={alertsOverTimeData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <RechartsTooltip content={<CustomTooltip />} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="count"
                  name="Alert Count"
                  stroke="#155EEF"
                  activeDot={{ r: 8 }}
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 3 }}>Alerts by Type</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={alertTypeData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  nameKey="name"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {alertTypeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* User Activity and Heat Map */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 3 }}>User Activity</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={userActivityData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <RechartsTooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="assets" name="Assets" fill="#155EEF" />
                <Bar dataKey="alerts" name="Alerts" fill="#F04438" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 3 }}>Asset Location Heat Map</Typography>
            {isLoaded ? (
              <Box sx={{ height: 300, width: '100%' }}>
                <GoogleMap
                  mapContainerStyle={{ height: '100%', width: '100%', borderRadius: '8px' }}
                  center={{ lat: -26.204103, lng: 28.047304 }} // Johannesburg
                  zoom={10}
                  onLoad={() => setMapLoaded(true)}
                >
                  {mapLoaded && isLoaded && heatmapData.length > 0 && (
                    <HeatmapLayer
                      data={heatmapData}
                      options={{
                        radius: 20,
                        opacity: 0.7,
                        gradient: [
                          'rgba(0, 255, 255, 0)',
                          'rgba(0, 255, 255, 1)',
                          'rgba(0, 191, 255, 1)',
                          'rgba(0, 127, 255, 1)',
                          'rgba(0, 63, 255, 1)',
                          'rgba(0, 0, 255, 1)',
                          'rgba(0, 0, 223, 1)',
                          'rgba(0, 0, 191, 1)',
                          'rgba(0, 0, 159, 1)',
                          'rgba(0, 0, 127, 1)',
                          'rgba(63, 0, 91, 1)',
                          'rgba(127, 0, 63, 1)',
                          'rgba(191, 0, 31, 1)',
                          'rgba(255, 0, 0, 1)'
                        ]
                      }}
                    />
                  )}
                </GoogleMap>
              </Box>
            ) : (
              <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography>Loading map...</Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Recent Alerts and Assets in Transit */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Recent Alerts</Typography>
              <Chip 
                label={`${recentAlerts.length} alerts`} 
                size="small" 
                color="primary" 
                variant="outlined" 
              />
            </Box>
            <List sx={{ width: '100%' }}>
              {recentAlerts.map((alert) => {
                const asset = mockAssets.find(a => a.id === alert.assetId);
                const user = mockUsers.find(u => u.id === alert.userId);
                
                return (
                  <React.Fragment key={alert.id}>
                    <ListItem alignItems="flex-start">
                      <ListItemAvatar>
                        <Avatar 
                          sx={{ 
                            bgcolor: alert.severity === 'critical' 
                              ? 'error.main' 
                              : alert.severity === 'warning' 
                                ? 'warning.main' 
                                : 'info.main' 
                          }}
                        >
                          {alert.severity === 'critical' 
                            ? <PriorityHighIcon /> 
                            : <WarningIcon />}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="subtitle2">
                              {alert.type} - {asset?.name}
                            </Typography>
                            <Chip
                              size="small"
                              label={alert.status}
                              color={alert.status === 'active' ? 'error' : 'success'}
                              variant="outlined"
                            />
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography variant="body2" color="text.primary">
                              {alert.message}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                              <PersonIcon fontSize="inherit" sx={{ mr: 0.5 }} />
                              {user?.name || 'Unknown User'} • {new Date(alert.timestamp).toLocaleString()}
                            </Typography>
                          </>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Tooltip title="View Details">
                          <IconButton edge="end" size="small">
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
                    <Divider variant="inset" component="li" />
                  </React.Fragment>
                );
              })}
              {recentAlerts.length === 0 && (
                <ListItem>
                  <ListItemText
                    primary="No recent alerts"
                    secondary="All systems operating normally"
                  />
                </ListItem>
              )}
            </List>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Assets in Transit</Typography>
              <Chip 
                label={`${assetsInTransit.length} assets`} 
                size="small" 
                color="primary" 
                variant="outlined" 
              />
            </Box>
            <List sx={{ width: '100%' }}>
              {assetsInTransit.map((asset) => {
                const user = mockUsers.find(u => u.id === asset.ownerId);
                const transitDetails = asset.transitDetails;
                
                return (
                  <React.Fragment key={asset.id}>
                    <ListItem alignItems="flex-start">
                      <ListItemAvatar>
                        <Avatar 
                          sx={{ 
                            bgcolor: asset.status === 'panic' 
                              ? 'error.main' 
                              : asset.status === 'warning' 
                                ? 'warning.main' 
                                : 'primary.main' 
                          }}
                        >
                          <DiamondIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="subtitle2">
                              {asset.name}
                            </Typography>
                            <Chip
                              size="small"
                              label={asset.status}
                              color={
                                asset.status === 'panic' 
                                  ? 'error' 
                                  : asset.status === 'warning' 
                                    ? 'warning' 
                                    : 'success'
                              }
                              variant="outlined"
                            />
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography variant="body2" color="text.primary">
                              {transitDetails.origin.name} → {transitDetails.destination.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                              <PersonIcon fontSize="inherit" sx={{ mr: 0.5 }} />
                              {user?.name || 'Unknown User'} • Speed: {transitDetails.currentSpeed} km/h
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              ETA: {new Date(transitDetails.estimatedArrival).toLocaleTimeString()}
                            </Typography>
                          </>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Tooltip title="View Details">
                          <IconButton edge="end" size="small">
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
                    <Divider variant="inset" component="li" />
                  </React.Fragment>
                );
              })}
              {assetsInTransit.length === 0 && (
                <ListItem>
                  <ListItemText
                    primary="No assets in transit"
                    secondary="All assets are stationary"
                  />
                </ListItem>
              )}
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardStats;
